import t from 'transit-js';

class BiMap {
    private readonly map: Map<string, string>;
    private readonly reverse = new Map<string, string>();
    constructor(map?: Map<string, string>) {
        if (map) {
            for (const [k, v] of map) {
                this.reverse.set(v, k);
            }
        } else {
            // just to not be undefined
            map = this.reverse;
        }
        this.map = map;
    }

    public get(key: string): string | undefined {
        return this.map.get(key);
    }
    public rev(value: string): string | undefined {
        return this.reverse.get(value);
    }
}

function namespacedKeyword(value: string, namespaces: BiMap): Keyword {
    const idx = value.indexOf('/');
    if (idx === -1) {
        return t.keyword(value);
    }
    const prefix = value.substring(0, idx);
    const namespace = namespaces?.get(prefix) ?? prefix;
    return t.keyword(namespace + value.substring(idx));
}
interface Keyword {
    name(): string
    namespace(): string | null
}
function parseKeyword(keyword: Keyword, namespaces: BiMap): string {
    const name = keyword.name();
    const namespace = keyword.namespace();
    if (namespace === null) {
        return name;
    }
    const prefix = namespaces.rev(namespace) ?? namespace;
    return prefix + '/' + name;
}

function toTransit<T>(m: T, namespaces: BiMap, keywordize?: Map<string, KeywordizeCommand>, root = '') {
    if (m instanceof Array) {
        return m.map((v) => {
            // todo support array paths
            return v;
        });
    } else if (m instanceof Object) {
        const data = t.map();
        for (const k in m) {
            const kc = keywordize?.get(root);
            const key = kc === null ? k : namespacedKeyword(k, namespaces);
            const v = m[k];
            const path = `${root}/${k}`; // allow over

            const vc = keywordize?.get(path);
            const keywordizeValue = vc !== undefined && typeof v === 'string' && (vc === '*' || vc?.has(v));
            if (keywordizeValue) {
                data.set(key, namespacedKeyword(v, namespaces));
            } else {
                if (kc === null && !keywordize?.has(path)) {
                    keywordize?.set(path, null);
                }
                const value = toTransit(v, namespaces, keywordize, path);
                data.set(key, value);
            }
        }
        return data;
    } else {
        return m;
    }
}
/* eslint-disable  @typescript-eslint/no-explicit-any */
function toJS<T>(data: any, namespaces: BiMap): T {
    if (t.isKeyword(data)) {
        return parseKeyword(data as Keyword, namespaces) as T;
    } else if (t.isMap(data)) {
        const map = {};
        for (const [k, v] of data) {
            const key = t.isKeyword(k) ? parseKeyword(k as Keyword, namespaces) : k;
            map[key] = toJS(v, namespaces);
        }
        return map as T;
    } else if (t.isList(data) || data instanceof Array) {
        const list = [];
        for (const e of data) {
            list.push(toJS(e, namespaces));
        }
        return list as T;
    }
    return data;
}

// exports
export type Codec<T, R> = {
    encode: (msg: T) => R;
    decode: (data: R) => T;
};

export type KeywordizeCommand
    = '*' // keywordize for all string values for ex {:type :cluster}
    | Set<string> // keywordize only specific values
    | null // stop keywordization (even for keys)
    ;

export function transit<T>(opts?: { keywordize?: Map<string, KeywordizeCommand>, verbose?: boolean, namespaces?: Map<string, string> }): Codec<T, string> {
    const type: t.Encodings = opts?.verbose ? 'json-verbose' : 'json';
    const namespaces = new BiMap(opts?.namespaces);
    return {
        encode: (msg: T): string => {
            const data = toTransit(msg, namespaces, opts?.keywordize);
            return t.writer(type).write(data);
        },
        decode: (data: string): T => {
            let map;
            try {
                map = t.reader(type).read(data);
            } catch (err) {
                throw new Error(`"${data}" is not valid TRANSIT`, {cause: err});
            }
            return toJS<T>(map, namespaces);
        }
    }
}

export function json<T>(): Codec<T, string> {
    return {
        encode: JSON.stringify,
        decode: JSON.parse
    }
}

function identity<T>(v: T): T {
    return v;
}

function transform<T>(v: T): T {
    // clojurescript compatibility with (js->clj)
    // https://github.com/clojure/clojurescript/blob/067eaef03678a06d83caf3f66ddf20f9ee71db5b/src/main/cljs/cljs/core.cljs#L11193

    // deep transform arrays
    if (v instanceof Array || Array.isArray(v)) {
        return v.map(transform) as T;
    }
    // deep transform objects (but not dates for example)
    if (v?.constructor === Object) {
        return Object.keys(v).reduce((a: T, k: string) => {
            a[k] = transform(v[k]);
            return a;
        }, {} as T);
    }
    // nullify undefined values
    if (v === undefined) {
        return null as T;
    }
    return v;
}

export function direct<T>(opts?: {cljs?: boolean}): Codec<T, T> {
    return {
        encode: identity,
        decode: (opts?.cljs ?? false) ? transform : identity
    }
}
